/* Native CSS 3D runs only while visible; JavaScript only updates intent and pointer pose. */
(() => {
  'use strict';
  const ready = callback => document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', callback, {once:true}) : callback();
  ready(() => {
    const selector = '[data-voralis-reserve]';
    const records = new Map(), stages = new Map(), pending = new Set();
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const fine = matchMedia('(hover: hover) and (pointer: fine)');
    const forced = matchMedia('(forced-colors: active)');
    const saveData = Boolean(navigator.connection?.saveData);
    const motion = document.body.classList.contains('motion-enabled');
    let raf = 0;
    const allowed = state => motion && !reduced.matches && !forced.matches && !document.hidden
      && state.visible && !state.userPaused && (!saveData || state.manualEnabled);
    const reset = state => {
      pending.delete(state); state.rect = null;
      state.chamber.style.removeProperty('--reserve-x');
      state.chamber.style.removeProperty('--reserve-y');
      if (!pending.size) {cancelAnimationFrame(raf);raf = 0;}
    };
    const update = state => {
      const running = allowed(state);
      state.root.dataset.animation = running ? 'running' : 'paused';
      if (state.toggle) {
        state.toggle.hidden = !motion || reduced.matches || forced.matches;
        state.toggle.setAttribute('aria-pressed', String(running));
        state.toggle.setAttribute('aria-label', running ? state.toggle.dataset.labelPause : state.toggle.dataset.labelPlay);
      }
      if (!running) reset(state);
    };
    const renderPointer = () => {
      raf = 0;
      const batch = [...pending]; pending.clear();
      batch.forEach(state => {
        if (allowed(state) && fine.matches && !state.rect) state.rect = state.stage.getBoundingClientRect();
      });
      batch.forEach(state => {
        if (!allowed(state) || !fine.matches || !state.rect) return;
        const rect = state.rect;
        const x = Math.max(-.5,Math.min(.5,(state.x-rect.left)/Math.max(1,rect.width)-.5));
        const y = Math.max(-.5,Math.min(.5,(state.y-rect.top)/Math.max(1,rect.height)-.5));
        state.chamber.style.setProperty('--reserve-x', `${(-5-y*12).toFixed(2)}deg`);
        state.chamber.style.setProperty('--reserve-y', `${(-7+x*18).toFixed(2)}deg`);
      });
    };
    const observeVisibility = entries => entries.forEach(entry => {
      const state = stages.get(entry.target);
      if (!state) return;
      state.visible = entry.isIntersecting && entry.intersectionRatio > .01;
      state.rect = null; update(state);
    });
    const observer = typeof IntersectionObserver === 'function'
      ? new IntersectionObserver(observeVisibility, {threshold:[0,.01,.15],rootMargin:'0px'}) : null;
    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(entries => entries.forEach(entry => {const state=stages.get(entry.target);if(state)state.rect=null;})) : null;
    const scan = scope => {
      const roots = [...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector)];
      roots.forEach(root => {
        if (records.has(root)) return;
        const stage=root.querySelector('[data-reserve-stage]'),chamber=root.querySelector('[data-reserve-chamber]');
        if (!stage || !chamber) return;
        const toggle=root.querySelector('[data-reserve-toggle]');
        const state={root,stage,chamber,toggle,visible:!observer,userPaused:false,manualEnabled:false,rect:null,x:0,y:0};
        records.set(root,state);stages.set(stage,state);
        observer?.observe(stage);resizeObserver?.observe(stage);update(state);
        toggle?.addEventListener('click', () => {
          if (!motion || reduced.matches || forced.matches) return;
          state.userPaused = allowed(state);
          state.manualEnabled = !state.userPaused;
          update(state);
        });
        stage.addEventListener('pointermove', event => {
          if (!allowed(state) || !fine.matches || event.pointerType==='touch') return;
          state.x=event.clientX;state.y=event.clientY;pending.add(state);
          if (!raf) raf=requestAnimationFrame(renderPointer);
        }, {passive:true});
        stage.addEventListener('pointerleave', () => reset(state), {passive:true});
      });
    };
    const refresh = () => records.forEach(update);
    reduced.addEventListener('change',refresh);forced.addEventListener('change',refresh);
    fine.addEventListener('change',() => records.forEach(reset));
    document.addEventListener('visibilitychange',refresh);
    // Scrolling only invalidates cached geometry; it never starts an animation loop.
    addEventListener('scroll',() => records.forEach(state => {state.rect=null;}),{passive:true});
    addEventListener('resize',() => records.forEach(state => {state.rect=null;}),{passive:true});
    addEventListener('pagehide',() => {
      records.forEach(state => {state.root.dataset.animation='paused';reset(state);});
    });
    addEventListener('pageshow',refresh);
    document.addEventListener('shopify:section:load',event => scan(event.target));
    document.addEventListener('shopify:section:unload',event => {
      records.forEach((state,root) => {
        if (root!==event.target && !event.target.contains(root)) return;
        reset(state);observer?.unobserve(state.stage);resizeObserver?.unobserve(state.stage);
        stages.delete(state.stage);records.delete(root);
      });
    });
    scan(document);
  });
})();
